import ReactMarkdown from 'react-markdown';

import { alpha, styled } from '@mui/material/styles';

import { markdownClasses } from './classes';

// ----------------------------------------------------------------------

const MARGIN = '0.75em';

export const StyledRoot = styled(ReactMarkdown)(({ theme }) => ({
  '> * + *': {
    marginTop: 0,
    marginBottom: MARGIN,
  },
  /**
   * Heading & Paragraph
   */
  h1: { ...theme.typography.editorText, marginTop: 40, marginBottom: 8 },
  h2: { ...theme.typography.editorText, marginTop: 40, marginBottom: 8 },
  h3: { ...theme.typography.editorText, marginTop: 24, marginBottom: 8 }, // Changed as per request, LLM model was sending h3 tags for headings which was too big.
  h4: { ...theme.typography.editorText, marginTop: 24, marginBottom: 8 },
  h5: { ...theme.typography.editorText, marginTop: 24, marginBottom: 8 },
  h6: { ...theme.typography.editorText, marginTop: 24, marginBottom: 8 },
  p: { ...theme.typography.editorText, margin: 0 },
  /**
   * Hr Divider
   */
  hr: {
    flexShrink: 0,
    borderWidth: 0,
    margin: '1em 0',
    msFlexNegative: 0,
    WebkitFlexShrink: 0,
    borderStyle: 'solid',
    borderBottomWidth: 'thin',
    borderColor: theme.palette.divider,
  },
  /**
   * Image
   */
  [`& .${markdownClasses.content.image}`]: {
    width: '100%',
    height: 'auto',
    maxWidth: '100%',
    margin: 'auto auto 1.25em',
  },
  /**
   * List
   */
  '& ul': {
    listStyleType: 'disc',
  },
  '& ul, & ol': {
    paddingLeft: 16,
    '& > li': {
      lineHeight: 2,
      fontSize: theme.typography.editorText.fontSize,
      '& > p': { margin: 0, display: 'inline-block' },
    },
  },
  /**
   * Blockquote
   */
  '& blockquote': {
    lineHeight: 1.5,
    fontSize: '1em',
    margin: '24px auto',
    position: 'relative',
    fontFamily: 'Georgia, serif',
    padding: theme.spacing(3, 3, 3, 8),
    color: theme.palette.text.secondary,
    borderLeft: `solid 8px ${alpha(theme.palette.grey[500], 0.08)}`,
    [theme.breakpoints.up('md')]: {
      width: '100%',
      maxWidth: 640,
    },
    '& p': {
      margin: 0,
      fontSize: 'inherit',
      fontFamily: 'inherit',
    },
    '&::before': {
      left: 16,
      top: -8,
      display: 'block',
      fontSize: '3em',
      content: '"\\201C"',
      position: 'absolute',
      color: theme.palette.text.disabled,
    },
  },
  /**
   * Code inline
   */
  [`& .${markdownClasses.content.codeInline}`]: {
    padding: theme.spacing(0.25, 0.5),
    color: theme.palette.text.primary,
    fontSize: theme.typography.editorText.fontSize,
    borderRadius: theme.shape.borderRadius / 2,
  },
  /**
   * Code Block
   */
  [`& .${markdownClasses.content.codeBlock}`]: {
    position: 'relative',
    '& pre': {
      overflowX: 'auto',
      padding: theme.spacing(3),
      color: theme.palette.common.black,
      borderRadius: theme.shape.borderRadius,
      backgroundColor: theme.palette.grey[300],
      '& code': { fontSize: theme.typography.editorText.fontSize },
    },
  },
  /**
   * Table
   */
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    border: `1px solid ${theme.palette.divider}`,
    'th, td': { padding: theme.spacing(1), border: `1px solid ${theme.palette.divider}`, fontSize: theme.typography.editorText.fontSize },
    th: {
      whiteSpace: 'nowrap',
    },
    'tbody tr:nth-of-type(odd)': { backgroundColor: theme.palette.background.neutral },
  },
  /**
   * Checkbox
   */
  input: {
    '&[type=checkbox]': {
      position: 'relative',
      cursor: 'pointer',
      '&:before': {
        content: '""',
        top: -2,
        left: -2,
        width: 17,
        height: 17,
        borderRadius: 3,
        position: 'absolute',
        backgroundColor: theme.palette.grey[300],
      },
      '&:checked': {
        '&:before': { backgroundColor: theme.palette.primary.main },
        '&:after': {
          content: '""',
          top: 1,
          left: 5,
          width: 4,
          height: 9,
          position: 'absolute',
          transform: 'rotate(45deg)',
          msTransform: 'rotate(45deg)',
          WebkitTransform: 'rotate(45deg)',
          border: `solid ${theme.palette.common.white}`,
          borderWidth: '0 2px 2px 0',
        },
      },
    },
  },
}));
